import { airports, type Airport } from '../data/airports';
import { blogPosts, type BlogPost } from '../data/blog-posts';
import { topicHubs, type TopicHub } from '../data/topic-hubs';

export const DOMAIN = 'https://airports.ng';
export const SITE_NAME = 'airports.ng';
export const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80';

export type SeoRouteKind =
  | 'home'
  | 'hub'
  | 'airport'
  | 'article'
  | 'tool'
  | 'policy';

export interface SeoRoute {
  path: string;
  title: string;
  description: string;
  canonical: string;
  lastmod: string;
  kind: SeoRouteKind;
  priority: number;
  changefreq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  image: string;
  aliases: string[];
  schema: Record<string, unknown>[];
}

export interface LegacyRedirect {
  source: string;
  destination: string;
  status: 301 | 302;
  force?: boolean;
}

export function canonicalPath(path: string) {
  return path === '/' ? '/' : `${path.replace(/\/+$/, '')}/`;
}

function absolute(path: string) {
  return `${DOMAIN}${canonicalPath(path)}`;
}

function expandAliases(aliases: string[]): string[] {
  const expanded = new Set<string>();
  for (const alias of aliases) {
    const clean = alias.replace(/\/$/, '');
    if (!clean) continue;
    expanded.add(clean);
    if (clean.endsWith('.html')) {
      expanded.add(clean.slice(0, -5));
    } else {
      expanded.add(clean + '.html');
    }
  }
  return Array.from(expanded);
}


function websiteSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: DOMAIN,
    description: "Nigeria's airport and travel information platform.",
    potentialAction: {
      '@type': 'SearchAction',
      target: `${DOMAIN}/airports/?search={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
}

function organizationSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: DOMAIN,
    logo: `${DOMAIN}/favicon.svg`,
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Editorial corrections and traveler support',
      email: 'hello@airports.ng',
      availableLanguage: ['English']
    }
  };
}

function breadcrumbSchema(items: Array<{ name: string; path: string }>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absolute(item.path)
    }))
  };
}

function airportSchema(airport: Airport): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Airport',
    name: airport.name,
    url: absolute(`/airport/${airport.id}`),
    description: airport.description,
    iataCode: airport.iataCode,
    icaoCode: airport.icaoCode,
    address: {
      '@type': 'PostalAddress',
      addressLocality: airport.city,
      addressRegion: airport.state,
      addressCountry: 'NG'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: airport.coordinates.lat,
      longitude: airport.coordinates.lng
    }
  };
}

function articleSchema(post: BlogPost): Record<string, unknown> {
  const absoluteImage = post.imageUrl.startsWith('http') ? post.imageUrl : `${DOMAIN}${post.imageUrl}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: absoluteImage,
    mainEntityOfPage: absolute(`/blog/${post.slug}`),
    datePublished: post.publishedDate,
    dateModified: post.lastUpdated,
    author: {
      '@type': 'Organization',
      name: 'airports.ng Editorial Team',
      url: absolute('/editorial-policy')
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${DOMAIN}/favicon.svg`
      }
    }
  };
}

function itemListSchema(name: string, path: string, items: Array<{ name: string; path: string }>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    url: absolute(path),
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: absolute(item.path)
    }))
  };
}

function topicHubSchema(hub: TopicHub): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: hub.h1,
    headline: hub.title,
    description: hub.description,
    url: absolute(hub.path),
    dateModified: hub.lastUpdated,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: DOMAIN
    },
    about: hub.facts.map(fact => fact.value),
    mainEntity: hub.faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

const staticRoutes: Array<Omit<SeoRoute, 'canonical' | 'schema' | 'aliases' | 'image'> & {
  aliases?: string[];
  image?: string;
  schema?: Record<string, unknown>[];
}> = [
  {
    path: '/',
    title: "airports.ng - Nigerian Airport Guides & Travel Tools",
    description: "Nigeria's comprehensive airport and travel information platform. Find airport guides, visa requirements, flight tools, passenger rights, and travel advice.",
    lastmod: '2026-10-23',
    kind: 'home',
    priority: 1,
    changefreq: 'weekly',
    aliases: ['/index.html']
  },
  {
    path: '/airports',
    title: 'Nigerian Airport Directory - Codes & Planning | airports.ng',
    description: 'Browse Nigerian airport locations, IATA and ICAO codes, practical terminal planning and links to official airport information.',
    lastmod: '2026-10-23',
    kind: 'hub',
    priority: 0.9,
    changefreq: 'weekly'
  },
  {
    path: '/blog',
    title: 'Nigerian Travel Blog & Airport Guides | airports.ng',
    description: 'Nigerian travel guides covering airport navigation, visa rules, domestic flights, passenger rights, travel safety, and airport services.',
    lastmod: '2026-10-23',
    kind: 'hub',
    priority: 0.9,
    changefreq: 'weekly',
    aliases: ['/travel.html', '/holiday-destinations.html', '/blog/index.html']
  },
  {
    path: '/passenger-rights',
    title: 'Passenger Rights in Nigeria - Complaints & Travel Guidance | airports.ng',
    description: 'Understand passenger rights in Nigeria, including flight delays, cancellations, baggage issues, visa rules, complaints, and airport procedures.',
    lastmod: '2026-10-23',
    kind: 'hub',
    priority: 0.8,
    changefreq: 'monthly'
  },
  {
    path: '/tools',
    title: 'Travel Tools - Flight Time Calculator, Currency Converter & More | airports.ng',
    description: 'Free travel tools for Nigerian trips, including a flight time calculator, currency converter, travel checklist, airport quiz, and airport management game.',
    lastmod: '2026-10-23',
    kind: 'tool',
    priority: 0.9,
    changefreq: 'monthly',
    aliases: ['/tools.html']
  },
  {
    path: '/flight-calculator',
    title: 'Flight Time Calculator - Nigerian Airports | airports.ng',
    description: 'Estimate straight-line distance and flight duration between Nigerian airports using disclosed planning assumptions. Confirm schedules with your airline.',
    lastmod: '2026-10-23',
    kind: 'tool',
    priority: 0.8,
    changefreq: 'monthly',
    aliases: ['/calculators.html']
  },
  {
    path: '/currency-converter',
    title: 'Currency Converter - Daily Indicative Exchange Rates | airports.ng',
    description: 'Estimate travel costs in eight currencies using dated daily reference rates, provider attribution, and clear stale-data warnings.',
    lastmod: '2026-10-23',
    kind: 'tool',
    priority: 0.7,
    changefreq: 'monthly'
  },
  {
    path: '/checklist',
    title: "Travel Checklist - Don't Forget Anything Important | airports.ng",
    description: 'Use a practical Nigerian travel checklist for passports, visas, health documents, luggage, electronics, and airport essentials.',
    lastmod: '2026-10-23',
    kind: 'tool',
    priority: 0.7,
    changefreq: 'monthly',
    aliases: ['/travel-checklist.html']
  },
  {
    path: '/quiz',
    title: 'Airport Quiz - Test Your Knowledge of Nigerian Airports | airports.ng',
    description: 'Test your knowledge of Nigerian airport codes, airport locations, IATA codes, and aviation facts with the airports.ng airport quiz.',
    lastmod: '2026-10-23',
    kind: 'tool',
    priority: 0.6,
    changefreq: 'monthly',
    aliases: ['/quiz.html']
  },
  {
    path: '/game',
    title: 'The Runway Boss - Airport Management Game | airports.ng',
    description: 'Play a lightweight airport management game where you manage gates, schedules, delays, and runway operations.',
    lastmod: '2026-10-23',
    kind: 'tool',
    priority: 0.6,
    changefreq: 'monthly',
    aliases: ['/game.html']
  },
  {
    path: '/about',
    title: 'About airports.ng - Independent Nigerian Airport Guides',
    description: 'Learn about airports.ng, our editorial standards, source review process, and mission to make airport information easier to use.',
    lastmod: '2026-10-23',
    kind: 'policy',
    priority: 0.8,
    changefreq: 'monthly',
    aliases: ['/about.html']
  },
  {
    path: '/contact',
    title: 'Contact Us - Get in Touch with airports.ng',
    description: 'Contact airports.ng for corrections, airport information updates, travel questions, editorial feedback, partnerships, and media inquiries.',
    lastmod: '2026-10-23',
    kind: 'policy',
    priority: 0.5,
    changefreq: 'monthly',
    aliases: ['/contact.html']
  },
  {
    path: '/privacy',
    title: 'Privacy Policy - How airports.ng Protects Your Data',
    description: 'Read the airports.ng privacy policy covering cookies, analytics, advertising, personal data, user rights, and contact details.',
    lastmod: '2026-10-23',
    kind: 'policy',
    priority: 0.3,
    changefreq: 'yearly',
    aliases: ['/privacy-policy.html']
  },
  {
    path: '/terms',
    title: 'Terms & Conditions - User Agreement | airports.ng',
    description: 'Read airports.ng terms and conditions, website usage rules, disclaimers, limitations, and user responsibilities.',
    lastmod: '2026-10-23',
    kind: 'policy',
    priority: 0.3,
    changefreq: 'yearly',
    aliases: ['/terms.html', '/disclaimer.html']
  },
  {
    path: '/advertising',
    title: 'Advertising Disclosure - How airports.ng Makes Money',
    description: 'Learn how airports.ng uses advertising, sponsored placements, affiliate relationships, and editorial independence safeguards.',
    lastmod: '2026-10-23',
    kind: 'policy',
    priority: 0.3,
    changefreq: 'yearly',
    aliases: ['/advertising.html']
  },
  {
    path: '/editorial-policy',
    title: 'Editorial Policy - airports.ng Content Standards',
    description: 'Review airports.ng editorial standards, fact-checking process, source policy, correction workflow, and content update cadence.',
    lastmod: '2026-10-23',
    kind: 'policy',
    priority: 0.3,
    changefreq: 'yearly',
    aliases: ['/editorial-policy.html']
  }
];

const airportLegacyAliases: Record<string, string[]> = {
  'lagos-murtala-muhammed': ['/airports/lagos-murtala-muhammed-international-airport.html'],
  'abuja-nnamdi-azikiwe': ['/airports/abuja-nnamdi-azikiwe-international-airport.html'],
  'port-harcourt-international': ['/airports/port-harcourt-international-airport.html'],
  'kano-international': [
    '/airports/kano-international-airport.html',
    '/airports/kano-mallam-aminu-kano-international-airport.html'
  ],
  'enugu-international': [
    '/airports/enugu-international-airport.html',
    '/airports/enugu-akanu-ibiam-international-airport.html'
  ],
  'calabar-international': ['/airports/calabar-international-airport.html'],
  'kaduna-international': ['/airports/kaduna-international-airport.html'],
  'maiduguri-international': [
    '/airports/maiduguri-international-airport.html',
    '/airports/maiduguri-airport.html'
  ],
  'benin-city': ['/airports/benin-city-airport.html'],
  ibadan: ['/airports/ibadan-airport.html'],
  ilorin: ['/airports/ilorin-airport.html'],
  'jos-yakubu-gowon': ['/airports/jos-airport.html'],
  bauchi: ['/airports/bauchi-airport.html'],
  gombe: ['/airports/gombe-airport.html'],
  makurdi: ['/airports/makurdi-airport.html'],
  warri: ['/airports/warri-airport.html', '/airports/osubi-airport.html', '/airport/osubi'],
  minna: ['/airports/minna-airport.html'],
  katsina: ['/airports/katsina-airport.html'],
  zaria: ['/airports/zaria-airport.html'],
  yola: ['/airports/yola-airport.html'],
  akure: ['/airports/akure-airport.html'],
  owerri: ['/airports/owerri-airport.html'],
  uyo: ['/airports/uyo-airport.html'],
  asaba: ['/airports/asaba-airport.html'],
  sokoto: ['/airports/sokoto-sadiq-abubakar-iii-airport.html']
};

const blogLegacyAliases: Record<string, string[]> = {
  'comprehensive-lagos-airport-guide-2026': [
    '/blog/comprehensive-lagos-airport-guide-2025.html',
    '/blog/comprehensive-lagos-airport-guide-2025'
  ],
  'best-airport-lounges-nigeria-business-travel': [
    '/blog/business-travel-nigeria-airport-lounges.html',
    '/blog/business-travel-nigeria-airport-lounges'
  ],
  'currency-exchange-nigeria-guide-2026': [
    '/blog/currency-exchange-money-tips-nigeria-travel.html',
    '/blog/currency-exchange-money-tips-nigeria-travel'
  ],
  'sustainable-aviation-nigeria-eco-friendly': [
    '/blog/15-sustainable-and-eco-friendly-travel-tips.html',
    '/blog/15-sustainable-and-eco-friendly-travel-tips'
  ],
  'travel-insurance-guide-nigeria': [
    '/blog/14-the-ultimate-guide-to-travel-insurance.html',
    '/blog/14-the-ultimate-guide-to-travel-insurance'
  ],
  'accessible-travel-nigeria-disability-guide': [
    '/blog/13-navigating-airports-with-disabilities.html',
    '/blog/13-navigating-airports-with-disabilities'
  ]
};

const staticSeoRoutes = staticRoutes.map(route => {
  const commonSchema = route.path === '/'
    ? [websiteSchema(), organizationSchema()]
    : [breadcrumbSchema([{ name: 'Home', path: '/' }, { name: route.title.replace(/ \| airports\.ng$/, ''), path: route.path }])];

  const image = route.image || DEFAULT_IMAGE;
  const absoluteImage = image.startsWith('http') ? image : `${DOMAIN}${image}`;

  return {
    ...route,
    canonical: absolute(route.path),
    aliases: expandAliases([...(route.aliases || []), ...(route.path === '/' ? [] : [`${route.path}.html`])]),
    image: absoluteImage,
    schema: route.schema || commonSchema
  };
});

const topicHubSeoRoutes: SeoRoute[] = topicHubs.map(hub => {
  const linkedItems = [...hub.primaryLinks, ...hub.relatedLinks]
    .filter(link => link.href.startsWith('/'))
    .map(link => ({ name: link.label, path: link.href }));

  const absoluteImage = hub.image.startsWith('http') ? hub.image : `${DOMAIN}${hub.image}`;

  return {
    path: hub.path,
    title: hub.title,
    description: hub.description,
    canonical: absolute(hub.path),
    lastmod: hub.lastUpdated,
    kind: 'hub',
    priority: 0.85,
    changefreq: 'monthly',
    image: absoluteImage,
    aliases: expandAliases([`${hub.path}.html`]),
    schema: [
      breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: hub.h1, path: hub.path }
      ]),
      topicHubSchema(hub),
      itemListSchema(`${hub.h1} supporting guides`, hub.path, linkedItems)
    ]
  };
});

const airportSeoRoutes: SeoRoute[] = airports.map(airport => {
  const path = `/airport/${airport.id}`;
  const absoluteImage = airport.imageUrl.startsWith('http') ? airport.imageUrl : `${DOMAIN}${airport.imageUrl}`;

  return {
    path,
    title: `${airport.name} (${airport.iataCode}) - Airport Guide | airports.ng`,
    description: `Location, airport codes, terminal planning and official sources for ${airport.name} (${airport.iataCode}).`,
    canonical: absolute(path),
    lastmod: airport.lastUpdated,
    kind: 'airport',
    priority: 0.9,
    changefreq: 'monthly',
    image: absoluteImage,
    aliases: expandAliases([`/airport/${airport.id}.html`, ...(airportLegacyAliases[airport.id] || [])]),
    schema: [
      breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Nigerian Airports', path: '/airports' },
        { name: `${airport.name} (${airport.iataCode})`, path }
      ]),
      airportSchema(airport)
    ]
  };
});

const blogSeoRoutes: SeoRoute[] = blogPosts.map(post => {
  const path = `/blog/${post.slug}`;
  const absoluteImage = post.imageUrl.startsWith('http') ? post.imageUrl : `${DOMAIN}${post.imageUrl}`;

  return {
    path,
    title: `${post.seoTitle || post.title} | airports.ng`,
    description: post.excerpt,
    canonical: absolute(path),
    lastmod: post.lastUpdated,
    kind: 'article',
    priority: 0.8,
    changefreq: 'monthly',
    image: absoluteImage,
    aliases: expandAliases([
      `/blog/${post.slug}.html`,
      ...(blogLegacyAliases[post.slug] || [])
    ]),
    schema: [
      breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Travel Blog', path: '/blog' },
        { name: post.title, path }
      ]),
      articleSchema(post)
    ]
  };
});

export const seoRoutes: SeoRoute[] = [
  ...staticSeoRoutes,
  ...topicHubSeoRoutes,
  ...blogSeoRoutes,
  ...airportSeoRoutes
];

export const legacyRedirects: LegacyRedirect[] = seoRoutes.flatMap(route => {
  const destination = canonicalPath(route.path);
  // Netlify matches /page and /page/ as the same source. Its Pretty URLs
  // handling supplies the trailing slash; an explicit rule would loop.
  return route.aliases
    .filter(source => !seoRoutes.some(page => canonicalPath(page.path) === canonicalPath(source)))
    .map(source => ({ source, destination, status: 301 as const, force: true }));
}).filter((redirect, index, redirects) =>
  redirects.findIndex(item => item.source === redirect.source) === index &&
  canonicalPath(redirect.source) !== canonicalPath(redirect.destination)
);

export const getSeoRouteByPath = (path: string): SeoRoute | undefined => {
  const pathname = new URL(path, DOMAIN).pathname.replace(/\/+$/, '') || '/';
  return seoRoutes.find(route => route.path === pathname || route.aliases.includes(pathname));
};

export const airportDirectorySchema = itemListSchema(
  'Nigerian Airports Directory',
  '/airports',
  airports.map(airport => ({ name: airport.name, path: `/airport/${airport.id}` }))
);

export const blogDirectorySchema = itemListSchema(
  'Nigerian Airport and Travel Guides',
  '/blog',
  blogPosts.map(post => ({ name: post.title, path: `/blog/${post.slug}` }))
);
